import { Box, Stack, Typography } from "@mui/material";
import { ReactNode, useState } from "react";
import { FeaturePermission } from "@/__generated__/featurePermissions";
import { useUser } from "@/auth/useUser";
import PaymentCardComponent from "@/components/serviceFlow/payments/paymentCardComponent";
import { useClientAccessToken } from "@/contexts/clientAccessTokenContext";
import { WalletQuery } from "@/graphql/queries/wallet.graphql.types";
import useWallet from "@/hooks/clients/useWallet";
import useCreditCardActions from "@/hooks/serviceFlow/wallet/useCreditCardActions";
import { useIsProviderOwner } from "@/hooks/user/useIsProviderOwner";
import AddNewCreditCardSection from "./addNewCreditCardSection";
import { LoadingState } from "./loadingState";

interface CardsStackProps {
  description?: string;
  clientId?: string;
  onlyDefaultCard?: boolean;
  noCardInfo?: ReactNode;
  noDefaultCardInfo?: ReactNode;
}

type Card = WalletQuery["clientByPk"]["stripeData"]["stripeCardsList"][number];

const sortByDefault = (currentCard: Card, nextCard: Card) => {
  if (nextCard.isDefault === currentCard.isDefault) return 0;
  return nextCard.isDefault ? 1 : -1;
};

/**
 * This component is used to display a stack of credit cards.
 * It is used in the wallet page and the client portal.
 * It is used to add a new credit card, delete a credit card, and set a default credit card.
 *
 * Since it uses Stripe's API to manage credit cards,
 * it needs to be wrapped in the AuthenticatedStripeProvider.
 */
export const CardsStack = ({
  description,
  clientId: clientIdFromProps,
  onlyDefaultCard = false,
  noCardInfo,
  noDefaultCardInfo,
}: CardsStackProps) => {
  const { clientAccessToken } = useClientAccessToken();
  const { isProviderOwner } = useIsProviderOwner();
  const { hasFeaturePermission, newPermissionsEnabledForUser } = useUser();
  const { cardList, loading, defaultCard } = useWallet(clientIdFromProps);
  const [showAddNewCardDrawer, setShowAddNewCardDrawer] = useState(false);

  const isProviderOwnerPermission = newPermissionsEnabledForUser
    ? hasFeaturePermission(FeaturePermission.IS_PROVIDER_OWNER)
    : isProviderOwner;

  const canManageCreditCards =
    !!clientAccessToken ||
    hasFeaturePermission?.(FeaturePermission.MANAGE_WALLET_CREDIT_CARDS);

  const {
    handleDeleteCard,
    handleSetDefaultCard,
    handleAttemptToDeleteDefaultCard,
  } = useCreditCardActions(clientIdFromProps);

  const hasCreditCards = onlyDefaultCard
    ? Boolean(defaultCard)
    : cardList.length > 0;

  const showNoCardsCopy = !loading && !hasCreditCards;

  const getCardsToDisplay = () => {
    if (onlyDefaultCard) {
      return defaultCard ? [defaultCard] : [];
    }
    return cardList;
  };

  const sortedCreditCards = getCardsToDisplay().sort(sortByDefault);

  return (
    <>
      {description && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="paragraphSmall">{description}</Typography>
        </Box>
      )}

      {loading && <LoadingState />}
      {showNoCardsCopy && !onlyDefaultCard && noCardInfo}
      {showNoCardsCopy && onlyDefaultCard && noDefaultCardInfo}

      {!loading && hasCreditCards && (
        <Stack spacing={3} sx={{ mt: 3 }}>
          {sortedCreditCards.map((card) => {
            const handleCardDelete = () => {
              if (card.isDefault && !isProviderOwnerPermission) {
                handleAttemptToDeleteDefaultCard(setShowAddNewCardDrawer);
              } else {
                handleDeleteCard(card.id);
              }
            };

            const handleCardSetDefault = card.isDefault
              ? undefined
              : () => handleSetDefaultCard(card.id);

            return (
              <PaymentCardComponent
                key={card.id}
                card={card}
                onDelete={handleCardDelete}
                onSetDefault={handleCardSetDefault}
              />
            );
          })}
        </Stack>
      )}

      {canManageCreditCards && (
        <AddNewCreditCardSection
          showDrawer={showAddNewCardDrawer}
          setShowDrawer={setShowAddNewCardDrawer}
          clientId={clientIdFromProps}
          setNewCardsAsDefault={onlyDefaultCard}
          isAnyCardDefault={Boolean(defaultCard)}
        />
      )}
    </>
  );
};
